"use client";

import Link from "next/link";
import { FiAlertCircle } from "react-icons/fi";
import { useT } from "@/hooks/useT";

interface SlippageWarningProps {
  /** User's `default_slippage_pct`. Pass `null`/`undefined` to render nothing. */
  slippagePct: number | null | undefined;
  /** bestAsk shown in the BUY/SELL button (0-1 range, e.g. 0.52 for 52¢). */
  currentPrice: number;
  side: "buy" | "sell";
  /** Threshold above which the warning fires. Default 10 (anything >10%). */
  threshold?: number;
  className?: string;
}

/**
 * Warns the user before they place a market order that their personal
 * slippage tolerance is high enough to let the CLOB walk the book well
 * past the displayed price. Renders nothing when slippage is at or
 * below the threshold (default 10%) — the 5% default is harmless.
 *
 * Origin: predictfren 2026-05-18 — user clicked 52¢, filled at 60¢,
 * thought it was a bug. It was their own `default_slippage_pct = 20`
 * setting allowing the walk. No UI surface communicated the tolerance,
 * so the trade-off (guaranteed fill ⇄ worse price) was invisible.
 */
export default function SlippageWarning({
  slippagePct,
  currentPrice,
  side,
  threshold = 10,
  className = "",
}: SlippageWarningProps) {
  const t = useT();
  const pct = Number(slippagePct);
  if (!Number.isFinite(pct) || pct <= threshold) return null;
  if (!Number.isFinite(currentPrice) || currentPrice <= 0 || currentPrice >= 1) return null;

  const mul = side === "buy" ? 1 + pct / 100 : 1 - pct / 100;
  // CLOB clamps to (tickSize, 1-tickSize). The exact tick is market-
  // specific (0.01, 0.001, 0.0001); 0.01 is the safest clamp for the
  // warning copy.
  const maxPrice = Math.min(0.99, Math.max(0.01, currentPrice * mul));
  const shownCents = (currentPrice * 100).toFixed(currentPrice < 0.1 ? 1 : 0);
  const maxCents = (maxPrice * 100).toFixed(maxPrice < 0.1 ? 1 : 0);

  return (
    <div className={`p-3 rounded-[5px] border border-red-500/40 bg-red-500/10 ${className}`}>
      <div className="flex items-start gap-2">
        <FiAlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-gray-800 dark:text-gray-100 leading-relaxed flex-1">
          <span className="font-semibold text-red-600 dark:text-red-400">
            {t("trade.slippageWarn.title")}
          </span>{" "}
          {t("trade.slippageWarn.body", {
            pct: pct.toFixed(0),
            maxCents,
            shownCents,
          })}{" "}
          <Link
            href="/settings"
            className="text-accent-primary hover:underline whitespace-nowrap"
          >
            {t("trade.slippageWarn.cta")}
          </Link>
        </div>
      </div>
    </div>
  );
}
